import {
    buildOrderSummary,
    shouldUsePricesWithDiscountsUrl,
} from '@auth0spa/util/ECommerceUtil';
import {
    CircularProgress,
    Typography,
} from '@mui/material';
import {
    createStyles,
    makeStyles,
} from '@mui/styles';
import React, {
    useCallback,
    useMemo,
} from 'react';
import {
    FormattedMessage,
    useIntl,
} from 'react-intl';
import useSWR from 'swr';

import { useLocalization } from '../../container/Localization';
import {
    getPricesWithDiscountsForUsBillingEntity,
    getSkuPackagesForUsBillingEntity,
} from '../../services/BillingService';
import { getOrganizationAppOriginFromEnv } from '../../util/EnvironmentUtil';
import {
    Features,
    useFeatureFlagValue,
} from '../../util/FeatureFlagUtil';
import {
    IDirectBuyData,
    IProductDiscount,
    IProductPricesWithDiscounts,
    ISkuPackage,
} from '../../util/interfaces/ecommerce';

const useStyles = makeStyles(theme =>
    createStyles({
        summaryContainer: {
            borderRadius: '3px',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: theme.palette.semantic.colorBorderDisabled,
            display: 'flex',
            flexDirection: 'column',
            margin: '16px 0',
            padding: '20px 15px',
        },
        row: {
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: 'row',
        },
        column: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
        },
        packRow: {
            fontWeight: 600,
            fontSize: '16px',
            color: theme.palette.semantic.colorForeground,
            marginBottom: '14px',
        },
        planTypeTitle: {
            fontSize: '14px',
            fontWeight: 600,
            color: theme.palette.semantic.colorForeground,
            marginBottom: '4px',
        },
        planTypeDescription: { color: theme.palette.semantic.colorForegroundDeEmp },
        centerLoader: { margin: 'auto' },
        emptyContainer: {
            margin: '16px 0',
            padding: '20px 15px',
        },
        percentageOff: {
            color: theme.palette.semantic.colorSuccessText,
            whiteSpace: 'pre',
        },
        savesPercentageOffText: {
            color: theme.palette.semantic.colorSuccessText,
            fontWeight: 400,
            fontSize: '12px',
            lineHeight: '16px',
        },
        priceText: {
            fontWeight: '400px',
            fontSize: '14px',
            lineHeight: '20px',
            color: theme.palette.semantic.colorForegroundLight,
            textDecorationLine: 'line-through',
            marginRight: '4px',
        },
        discountRow: {
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'row',
        },
    }),
);

export const DirectBuyOrderSummaryComponent: React.FC<{
    directBuySku: string;
    directBuyData: IDirectBuyData | undefined;
}> = ({
    directBuySku, directBuyData,
}) => {
    const classes = useStyles();
    const { formatMessage: translate } = useIntl();
    const language = useLocalization(true);
    const { EnableEcommercePromotionalDiscounts } = useFeatureFlagValue([ Features.EnableEcommercePromotionalDiscounts ]);
    const { EnableEcommerceRobotUnits } = useFeatureFlagValue([ Features.EnableEcommerceRobotUnits ]);

    const portalUrl = useMemo(() => getOrganizationAppOriginFromEnv(), []);
    const usePricesWithDiscountsUrl = useMemo(() =>
        shouldUsePricesWithDiscountsUrl(EnableEcommerceRobotUnits, directBuyData),
    [ EnableEcommerceRobotUnits, directBuyData ]);
    const {
        data: skuPackageDetails, error: skuPackageDetailsError,
    } = useSWR<ISkuPackage[], Error>(
        portalUrl != null && !usePricesWithDiscountsUrl ? [
            portalUrl,
            '/sku-package-prices?countrycode=US',
            usePricesWithDiscountsUrl,
        ] : null,
        getSkuPackagesForUsBillingEntity,
    );
    const productConfigListRequest = useMemo<string[]>(() => {
        return Object.keys(directBuyData?.productQuantities ?? {});
    }, [ directBuyData ]);
    const {
        data: pricesWithDiscounts, error: pricesWithDiscountsError,
    } = useSWR<IProductPricesWithDiscounts, Error>(
        portalUrl != null && usePricesWithDiscountsUrl ?
            [
                'prices-with-discounts',
                portalUrl,
                directBuyData?.discountCode,
                productConfigListRequest,
                usePricesWithDiscountsUrl,
            ] : null,
        () => getPricesWithDiscountsForUsBillingEntity(portalUrl, productConfigListRequest, directBuyData?.discountCode),
    );

    const getProductDiscount = useCallback((sku: ISkuPackage, productDiscount: IProductDiscount) => {
        const quantity = sku.products?.filter(product => product.code === productDiscount.code)[0].quantity;
        return quantity * productDiscount.discountedPrice;
    }, []);

    const {
        directBuySkuInfo, directBuySkuDiscount,
    } = useMemo(
        () => {
            const sku = skuPackageDetails?.filter(skuInfo => skuInfo.type === directBuySku)[0];
            const couponIds = sku?.productDiscounts.map(d => d.couponId);
            const uniqueCouponIds = [ ...new Set(couponIds) ];
            const discountedPackagePrice = sku?.productDiscounts
                .reduce((prev, currentDiscount) => prev + getProductDiscount(sku, currentDiscount), 0);
            const discount = uniqueCouponIds.length === 1 ? sku?.productDiscounts[0] : undefined;
            if (discount) {
                discount.discountedPrice = discountedPackagePrice ?? 0;
            }

            return {
                directBuySkuInfo: sku,
                directBuySkuDiscount: discount,
            };
        },
        [ directBuySku, skuPackageDetails, getProductDiscount ],
    );

    const getPriceString = useCallback((price: number, currency: string) => {
        return `${(price / 100).toLocaleString(language, {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })}`;
    }, [ language ]);

    const orderSummary = useMemo(() =>
        buildOrderSummary(
            EnableEcommerceRobotUnits,
            EnableEcommercePromotionalDiscounts,
            directBuySkuInfo,
            directBuySkuDiscount,
            pricesWithDiscounts,
            directBuyData,
        ),
    [
        EnableEcommercePromotionalDiscounts,
        EnableEcommerceRobotUnits,
        directBuyData,
        directBuySkuDiscount,
        directBuySkuInfo,
        pricesWithDiscounts,
    ]);

    return orderSummary
        ?
        <div
            className={classes.summaryContainer}
            data-cy='direct-buy-sku-preview'>
            <div className={classes.row}>
                <Typography className={classes.packRow}>
                    {translate({ id: `CLIENT_SKU_PACKAGE_PACK_${directBuySku}` })}
                </Typography>
                {!orderSummary.shouldDisplayDiscount && (
                    <Typography>
                        {translate({ id: `CLIENT_PRICE_PER_MONTH` }, { price: getPriceString(orderSummary.price, orderSummary.currency) })}
                    </Typography>
                )}

                {orderSummary.shouldDisplayDiscount && (
                    <div className={classes.column}>
                        <Typography data-cy="direct-buy-sku-discounted-price">
                            {translate({ id: `CLIENT_PRICE_PER_MONTH` }, { price: getPriceString(orderSummary.discountedPrice, orderSummary.currency) })}
                        </Typography>
                        <div className={classes.discountRow}>
                            <Typography
                                className={classes.priceText}
                                data-cy="direct-buy-sku-full-price">
                                {getPriceString(orderSummary.price, orderSummary.currency)}
                            </Typography>
                            <Typography
                                className={classes.savesPercentageOffText}
                                data-cy="direct-buy-sku-saves-percentage-off">
                                <FormattedMessage
                                    id="CLIENT_ORDER_SUMMARY_SAVES_PERCENTAGE"
                                    values={{ percentageOff: orderSummary?.percentageOff ?? 0 }}
                                />
                            </Typography>
                        </div>
                    </div>
                )}
            </div>

            <Typography className={classes.planTypeTitle}>
                {translate({ id: 'CLIENT_PLAN_TYPE' })}
            </Typography>
            <Typography className={classes.planTypeDescription}>
                {translate({ id: 'CLIENT_MONTHLY_PLAN' })}
            </Typography>
            {orderSummary.shouldDisplayDiscount && (<Typography
                className={classes.planTypeDescription}
                data-cy="direct-buy-sku-discount-message">
                <FormattedMessage
                    id="CLIENT_ORDER_SUMMARY_DISCOUNT"
                    values={{
                        green: (chunk: string) => <span className={classes.percentageOff}>
                            {chunk}
                        </span>,
                        percentageOff: orderSummary.percentageOff ?? 0,
                        durationInMonths: orderSummary.durationInMonths ?? 0,
                    }}
                />
            </Typography>)}
        </div>
        :
        ((skuPackageDetailsError || pricesWithDiscountsError)
            ?
            (<div className={classes.emptyContainer} />)
            : (<div className={classes.centerLoader}>
                <CircularProgress />
            </div>)
        );
};
